import { ComponentRef, EventEmitter, Injectable, Renderer2, Type, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, of, take, takeUntil, withLatestFrom } from 'rxjs';
import { StepMenuService } from './services/step-menu.service';
import { markFormGroupTouched } from '@core/helpers';
import { IMenuBase, IAbstractPresenter, IMenu } from '@core/interfaces';
import { StepComponent } from './interfaces/step-component';
import { IStepMenuComponent, IStepMenuPresenter } from './interfaces/step-menu-component.interface';

@Injectable({
	providedIn: 'root',
})
export class StepMenuPresenter<T, K>
	extends IAbstractPresenter<IStepMenuComponent<T, K>>
	implements IStepMenuPresenter<T, K>
{
	activeStep = 0;
	stepTo = 0;
	lastStep = 0;
	currentInstance!: IMenu<T, K>;
	formValid$: Observable<boolean>;
	loading$: Observable<boolean> = of(false);
	currentStep: BehaviorSubject<number> = new BehaviorSubject(0);

	isStepper = true;
	showButtonCancel = false;
	buttomFinishName!: string;
	buttomFinishIco: string | undefined;
	routeItems: IMenu<T, K>[] = [];
	stepChange$: Observable<number> = of(0);
	isAnimating = false;
	isFinishStep = false;
	bodyTarget!: ViewContainerRef;

	private currentRef?: ComponentRef<
		T & {
			stepId?: number;
		}
	>;

	activeItemChange = new EventEmitter<IMenu<T, K>>();
	cancelChange = new EventEmitter<void>();
	finishChange = new EventEmitter<void>();
	instanceCreated = new EventEmitter<T>();
	backToHome = new EventEmitter<void>();
	isFinishStepChange = new EventEmitter<boolean>();

	constructor(
		private stepMenuService: StepMenuService,
		private renderer: Renderer2
	) {
		super();

		this.formValid$ = this.stepMenuService.formValid$;
	}

	dispatchActions(): void {}

	override onAfterViewInit(): void {
		this.renderBody('next');
	}

	suscribeObservers(): void {
		this.view.stepChange$.pipe(takeUntil(this.destroy$)).subscribe(step => this.onStepChange(step));
	}

	override onDestroy(): void {
		super.onDestroy();

		this.onStepChange(0);
		this.view.bodyTarget?.clear();
	}

	cancelStep(): void {
		this.view.cancelChange.emit();
		this.view.activeStep = 0;
	}

	prevStep(): void {
		this.onStepChange(this.findPrevStepAvailable(this.view.activeStep));
	}

	nextStep(): void {
		this.onStepChange(this.findNextStepAvailable(this.view.activeStep));
	}

	finishStep(): void {
		this.stepMenuService.instances$.pipe(take(1)).subscribe(instances => {
			const instance = instances[this.view.activeStep];
			const routeItem = this.view.routeItems[this.view.activeStep] as IMenu<T>;

			if (instance) markFormGroupTouched(instance?.formGroup, true);
			if (!!instance?.formGroup?.valid || routeItem?.validateInstance?.() || instance?.validateInstance?.() || !instance?.formGroup)
				this.view.finishChange.emit();
		});
	}

	onComponentInstanceCreated({ instance, stepId }: { instance: T; stepId: number }): void {
		this.stepMenuService.registerForm(instance as StepComponent, stepId);
		const routeItem = this.view.routeItems[stepId] as IMenu<T>;

		if (routeItem?.data) {
			Object.entries(routeItem?.data ?? {}).map(([key, value]) => {
				this.currentRef?.setInput(key, value);
			});
		}

		routeItem?.suscribeObservers?.(instance as IMenuBase);
	}

	findNextStepAvailable(currentStep: number): number {
		let step = currentStep + 1;
		while (this.view.routeItems[step]?.disabled ?? false) {
			step++;
		}

		if (this.validateStepLimit(step)) return currentStep;

		return step;
	}

	findPrevStepAvailable(currentStep: number): number {
		let step = currentStep - 1;
		while (this.view.routeItems[step]?.disabled ?? false) {
			step--;
		}

		if (this.validateStepLimit(step)) return currentStep;

		return step;
	}

	onStepChange(nextStep: number): void {
		const prevStep = this.view.lastStep;
		const isDecreasing = nextStep < prevStep;

		this.stepMenuService
			.validatePreviousInstances<T>(prevStep, isDecreasing, this.view.routeItems)
			.pipe(
				withLatestFrom(this.view.loading$),
				filter(([_, loading]) => !loading),
				map(([valid]) => {
					// TO DO FALTA ARREGLAR CUANDO SE PASA DEL STEP 0 AL 2
					console.log('Is Decrease ', isDecreasing, ' Previus: ', prevStep, ' Next: ', nextStep, 'Valid: ', valid)
					if (!valid || nextStep === prevStep) return;
					
					this.view.lastStep = nextStep;
					this.view.activeStep = nextStep;
					this.stepMenuService.changeStep(nextStep);
					this.view.activeItemChange.emit(this.view.routeItems[nextStep]);
					this.currentStep.next(nextStep);

					const direction = nextStep > prevStep ? 'next' : 'prev';
					this.renderBody(direction);
				}),
				takeUntil(this.destroy$),
				take(1)
			).subscribe();
	}

	validateStepLimit(step: number): boolean {
		const routeLength = this.view.routeItems.length - 1;

		if (step < 0 || step > routeLength) {
			if (routeLength === this.view.activeStep)
				this.finishStep();

			if(step <= 0){
				this.goBackToHome();
			}
		
			return true;
		}

		return false;
	}

	updateStepMenu(tabMenuName: string, disable: boolean): void {
		const routeItem = this.view.routeItems.find(route => route.name === tabMenuName);

		if (routeItem) routeItem.disabled = disable;
	}

	trackByRouteItem(index: number, _item: IMenu<T, K>): number {
		return index;
	}

	private readonly SLIDE = {
		next: { out: 'slide-out-left', in: 'slide-in-right' },
		prev: { out: 'slide-out-right', in: 'slide-in-left' },
	} as const;

	private renderBody(direction: 'next' | 'prev' = 'next'): void {
		const ANIM_MS = 200;

		if (!this.view?.bodyTarget) return;

		const step = this.view.routeItems[this.view.activeStep];

		if (!step?.component) return;

		const oldNode = this.currentRef?.location.nativeElement as HTMLElement | undefined;

		if (!oldNode) return this.mountComponent(step.component, null);

		const classes = this.SLIDE[direction];

		this.renderer.removeAttribute(oldNode, 'class');
		this.renderer.addClass(oldNode, classes.out);

		setTimeout(() => {
			this.mountComponent(step.component, direction);
		}, ANIM_MS);
	}

	private mountComponent(
		component: Type<T & { stepId?: number }> | undefined,
		direction: 'next' | 'prev' | null
	): void {
		if (!component || !this.view?.bodyTarget) return;

		this.view.bodyTarget.clear();
		this.currentRef = this.view.bodyTarget.createComponent(component);

		this.onComponentInstanceCreated({ instance: this.currentRef.instance, stepId: this.view.activeStep });

		const routeLength = this.view.routeItems.length - 1;
		
		if (this.view.activeStep === routeLength) {
			this.view.isFinishStep = true;
		} else {
			this.view.isFinishStep = false;
		}
		
		this.isFinishStepChange.emit(this.view.isFinishStep);
		
		if (!direction) return;

		const newHost = this.currentRef?.location.nativeElement as HTMLElement;
		const classes = this.SLIDE[direction];

		requestAnimationFrame(() => {
			this.renderer.addClass(newHost, classes.in);
		});
	}

	goBackToHome(): void {
		this.view.backToHome.emit();
	}
}
